// src/components/complaints/ComplaintDetails.js
import React, { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase.config';

function ComplaintDetails({ complaint, userData, isAdmin, onBack }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [status, setStatus] = useState(complaint.status);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from('complaint_comments')
        .select(`
          *,
          user:residents!complaint_comments_user_id_fkey(name, flat_number)
        `)
        .eq('complaint_id', complaint.id)
        .order('created_at', { ascending: false });

      if (!error) {
        setComments(data || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const { error } = await supabase
        .from('complaint_comments')
        .insert([{
          complaint_id: complaint.id,
          user_id: userData.id,
          comment: newComment,
          created_at: new Date().toISOString()
        }]);

      if (!error) {
        setNewComment('');
        fetchComments();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleStatusUpdate = async () => {
    setLoading(true);
    try {
      const client = isAdmin ? supabaseAdmin : supabase;
      
      const updateData = {
        status,
        updated_at: new Date().toISOString()
      };

      if (status === 'resolved' && resolutionNotes) {
        updateData.resolution_notes = resolutionNotes;
        updateData.resolved_at = new Date().toISOString();
        updateData.resolved_by = userData.id;
      }

      const { error } = await client
        .from('complaints')
        .update(updateData)
        .eq('id', complaint.id);

      if (!error) {
        alert('Status updated successfully!');
        onBack();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      acknowledged: '#17a2b8',
      in_progress: '#fd7e14',
      resolved: '#28a745',
      closed: '#6c757d'
    };
    return colors[status] || '#6c757d';
  };

  return (
    <div style={{ padding: '20px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '20px'
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '10px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          ←
        </button>
        <h2 style={{ margin: 0 }}>Complaint Details</h2>
      </div>

      {/* Main Details */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3>{complaint.subject}</h3>
            <p style={{ color: '#666', lineHeight: '1.6' }}>{complaint.description}</p>
            
            <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
              <div>
                <strong>Complaint No:</strong> #{complaint.complaint_number}
              </div>
              <div>
                <strong>Category:</strong> {complaint.category}
              </div>
              <div>
                <strong>Status:</strong> 
                <span style={{
                  marginLeft: '10px',
                  padding: '3px 10px',
                  background: getStatusColor(complaint.status) + '20',
                  color: getStatusColor(complaint.status),
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {complaint.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div>
                <strong>Urgency:</strong> {complaint.urgency}
              </div>
              <div>
                <strong>Location:</strong> {complaint.location || 'N/A'}
              </div>
              <div>
                <strong>Created By:</strong> {complaint.creator?.name} ({complaint.creator?.flat_number})
              </div>
              <div>
                <strong>Created On:</strong> {new Date(complaint.created_at).toLocaleString()}
              </div>
              {complaint.resolved_at && (
                <div>
                  <strong>Resolved On:</strong> {new Date(complaint.resolved_at).toLocaleString()}
                </div>
              )}
            </div>

            {complaint.resolution_notes && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: '#e8f5e9',
                borderRadius: '8px'
              }}>
                <strong>Resolution Notes:</strong>
                <p style={{ margin: '10px 0 0 0' }}>{complaint.resolution_notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Update (Admin Only) */}
      {isAdmin && complaint.status !== 'closed' && (
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <h4>Update Status</h4>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                flex: 1,
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px'
              }}
            >
              <option value="pending">Pending</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          
          {status === 'resolved' && (
            <textarea
              placeholder="Resolution notes..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                marginBottom: '10px',
                minHeight: '80px'
              }}
            />
          )}
          
          <button
            onClick={handleStatusUpdate}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Updating...' : 'Update Status'}
          </button>
        </div>
      )}

      {/* Comments Section */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        <h4>Comments & Updates</h4>
        
        <div style={{ marginBottom: '20px' }}>
          <textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              minHeight: '60px'
            }}
          />
          <button
            onClick={handleAddComment}
            style={{
              marginTop: '10px',
              padding: '8px 20px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Add Comment
          </button>
        </div>

        <div>
          {comments.length === 0 ? (
            <p style={{ color: '#999' }}>No comments yet</p>
          ) : (
            comments.map(comment => (
              <div key={comment.id} style={{
                padding: '10px',
                borderBottom: '1px solid #eee',
                marginBottom: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <strong>{comment.user?.name} ({comment.user?.flat_number})</strong>
                  <small style={{ color: '#999' }}>
                    {new Date(comment.created_at).toLocaleString()}
                  </small>
                </div>
                <p style={{ margin: 0 }}>{comment.comment}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ComplaintDetails;